/**
 * File Upload Hook
 * 
 * Manages file upload functionality including file reading and JSON parsing.
 */

import { useState, useRef, useCallback } from 'react';
import { parseProjectData, validateProjectData } from '../utils/jsonParser';

/**
 * Custom hook for handling file uploads
 * 
 * @returns {object} File upload state and handlers
 */
export function useFileUpload() {
  const [projectData, setProjectData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  
  /**
   * Handle file selection and parsing
   */
  const handleFileChange = useCallback((file) => {
    if (!file) return;
    
    // Reset state
    setError(null);
    setIsLoading(true);
    
    // Check file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'json') {
      setError('Please upload a .json file');
      setIsLoading(false);
      return;
    }
    
    // Read file
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const rawData = JSON.parse(event.target.result);
        
        // Validate structure
        const validation = validateProjectData(rawData);
        if (!validation.valid) {
          setError(validation.error);
          setIsLoading(false);
          return;
        }
        
        // Parse data
        const parsedData = parseProjectData(rawData);
        setProjectData(parsedData);
        setIsLoading(false);
      } catch (err) {
        setError(`Failed to parse JSON: ${err.message}`);
        setIsLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Failed to read file');
      setIsLoading(false);
    };
    
    reader.readAsText(file, 'UTF-8');
  }, []);
  
  /**
   * Trigger file input click
   */
  const triggerUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  
  /**
   * Handle file input change event
   */
  const onInputChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  }, [handleFileChange]);
  
  /**
   * Handle drag and drop
   */
  const onFileDrop = useCallback((file) => {
    handleFileChange(file);
  }, [handleFileChange]);
  
  /**
   * Reset upload state
   */
  const reset = useCallback(() => {
    setProjectData(null);
    setError(null);
    setIsLoading(false);
  }, []);
  
  return {
    // State
    projectData,
    isLoading,
    error,
    
    // Refs
    fileInputRef,
    
    // Handlers
    triggerUpload,
    onInputChange,
    onFileDrop,
    reset,
  };
}


